import type {
    AnatomyBinding,
    AnatomyPart,
    AnatomyState,
    ComponentAnatomySpec,
} from "./componentAnatomy";
import { inputSizes } from "./componentAnatomyInputSizes";
import { inputVariantSpecs } from "./componentAnatomyInputVariants";

// Mined from designSystem/Input/Input.module.scss: the .root base block here,
// the size axis in componentAnatomyInputSizes, the colour layer in
// componentAnatomyInputVariants.

const parts: readonly AnatomyPart[] = [
    {
        id: "background",
        label: "Container",
        element: "background-color on .root, via --input-bg",
    },
    {
        id: "border",
        label: "Border",
        element: "1px border always reserved on .root, via --input-border",
    },
    {
        id: "label",
        label: "Text / placeholder",
        element:
            "color on .root inherited by .control, via --input-fg / --input-placeholder",
    },
    {
        id: "glyph",
        label: "Icon slot",
        element: ".slot wrapping a 16px icon, inheriting --input-fg",
    },
    {
        id: "xButton",
        label: "Clear button",
        element: "24×24 .clearButton [data-slot-box] in the right slot",
    },
    {
        id: "focusRing",
        label: "Focus ring",
        element: "box-shadow on .root while .control is focused",
    },
];

const states: readonly AnatomyState[] = [
    { id: "resting", label: "Default", selector: "" },
    {
        id: "hover",
        label: "Hover",
        selector:
            ":hover:not(:has(.control:disabled)):not([data-disabled]):not(:has(.control:read-only)):not(:has(.control:focus))",
    },
    { id: "focus", label: "Focus", selector: ":has(.control:focus)" },
    {
        id: "readOnly",
        label: "Read-only",
        selector: ":has(.control:read-only)",
    },
    { id: "disabled", label: "Disabled", selector: ":has(.control:disabled)" },
];

const sharedBindings: readonly AnatomyBinding[] = [
    { partId: "border", property: "border-width", token: "--border-width" },
    {
        partId: "border",
        property: "border-radius",
        token: "--radius-m",
        via: "--input-radius",
    },
    { partId: "glyph", property: "width", literal: "16px" },
    { partId: "glyph", property: "height", literal: "16px" },
    { partId: "xButton", property: "width", literal: "24px" },
    { partId: "xButton", property: "height", literal: "24px" },
    { partId: "xButton", property: "icon width", literal: "16px" },
    { partId: "xButton", property: "icon height", literal: "16px" },
    { partId: "xButton", property: "border-radius", token: "--radius-s" },
    {
        partId: "xButton",
        property: "background-color (hover)",
        token: "--bg-neutral-hover",
    },
    {
        partId: "background",
        property: "padding-inline (with slot box)",
        literal: "calc(var(--input-padding-x) - var(--space-4))",
        note: "A [data-slot-box] child trims its side by one step so a 24px box centres where a 16px icon would.",
    },
];

export const inputAnatomy: ComponentAnatomySpec = {
    label: "Input",
    schematic: "input",
    rootSelector: ".root",
    variantAxisLabel: "Variant",
    parts,
    states,
    sharedBindings,
    variants: inputVariantSpecs,
    sizes: inputSizes,
};
